import { useCallback, useRef } from 'react';
import { UpdateBlockContentCommand } from '../UndoRedo/UpdateBlockContentCommand';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import type { DimensionPositionStateChangedType } from './UseBlockDimensionChangedHandler';
import { getRoundedValue } from '../GridDndEditor/gridHelper';
import { ALLOWED_DECIMAL_PLACES_FOR_DIMENSION } from './useNewBlockDimensionChangedHandler';

export function useNewBlockContentChangedHandler() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const snapshotRef = useRef<string | null>(null);

  const captureBeforeEdit = useCallback((currentContent: string) => {
    snapshotRef.current = currentContent;
  }, []);

  const commitEdit = useCallback(
    (blockId: string, sectionId: string, newContent: string): void => {
      const previousContent = snapshotRef.current;
      snapshotRef.current = null;

      if (previousContent === null) {
        console.warn('useBlockContentUndoRedo: commitEdit called without captureBeforeEdit');
        return;
      }

      if (previousContent === newContent) return;

      const command = new UpdateBlockContentCommand(
        { dispatch, socketClient, userId, checkDocumentLockStatus },
        { blockId, sectionId, previousContent, newContent }
      );

      command.applyOptimistic();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);

      command.persistToSocket().then((success) => {
        if (!success) {
          console.error('useBlockContentUndoRedo: socket persist failed, state was rolled back');
          undoRedoManager.clearBlockCommands(blockId);
        }
      });
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );

  const handleBlockContentChanged = useCallback(
    (
      blockId: string,
      sectionId: string,
      previousContent: string,
      newContent: string,
      previousDimension?: DimensionPositionStateChangedType,
      newDimension?: DimensionPositionStateChangedType
    ): void => {
      const isContentSame = previousContent === newContent;
      const isDimensionSame =
        !previousDimension ||
        !newDimension ||
        (previousDimension.widthPx === newDimension.widthPx &&
          previousDimension.heightPx === newDimension.heightPx &&
          previousDimension.xAxisPx === newDimension.xAxisPx &&
          previousDimension.yAxisPx === newDimension.yAxisPx);

      if (isContentSame && isDimensionSame) return;

      const formattedPreviousDimension = previousDimension
        ? {
            ...previousDimension,
            widthPx: getRoundedValue(previousDimension.widthPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
            heightPx: getRoundedValue(previousDimension.heightPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
          }
        : undefined;

      const formattedNewDimension = newDimension
        ? {
            ...newDimension,
            widthPx: getRoundedValue(newDimension.widthPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
            heightPx: getRoundedValue(newDimension.heightPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
          }
        : undefined;

      const command = new UpdateBlockContentCommand(
        { dispatch, socketClient, userId, checkDocumentLockStatus },
        {
          blockId,
          sectionId,
          previousContent,
          newContent,
          previousDimension: formattedPreviousDimension,
          newDimension: formattedNewDimension,
        }
      );

      command.applyOptimistic();

      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
      undoRedoManager.pushUndoRedoCommands(command);

      command.persistToSocket().then((success) => {
        if (!success) {
          console.error('useBlockContentUndoRedo: socket persist failed, state was rolled back');
          undoRedoManager.clearBlockCommands(blockId);
        }
      });
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );

  return { captureBeforeEdit, commitEdit, handleBlockContentChanged };
}
